import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../Firebase";
import {
  addDoc,
  collection,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import "../styles/AddCourse.css";
import { toast } from "react-toastify";

const AddCourse = () => {
  /* ================= BASIC STATES ================= */
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [thumbnail, setThumbnail] = useState(null);

  /* ================= EXTRA STATES ================= */
  const [category, setCategory] = useState("");
  const [isFree, setIsFree] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [freePreview, setFreePreview] = useState(false);

  /* ================= SECTIONS ================= */
  const [sections, setSections] = useState([
    { title: "", description: "" }
  ]);

  /* ================= FAQ ================= */
  const [faqs, setFaqs] = useState([
    { q: "", a: "" }
  ]);

  const navigate = useNavigate();
  const storage = getStorage();

  /* ================= SECTION HANDLERS ================= */
  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const addSection = () => {
    setSections([...sections, { title: "", description: "" }]);
  };

  const removeSection = index => {
    setSections(sections.filter((_, i) => i !== index));
  };

  /* ================= FAQ HANDLERS ================= */
  const handleFaqChange = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addFaq = () => {
    setFaqs([...faqs, { q: "", a: "" }]);
  };

  const removeFaq = index => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  /* ================= SUBMIT ================= */
  const handleAddCourse = async () => {
    const user = auth.currentUser;
    if (!user) return navigate("/login");

    try {
      const basePrice = isFree ? 0 : Number(price);
      const discountedPrice =
        !isFree && discountPercent
          ? Math.round(
              basePrice - (basePrice * discountPercent) / 100
            )
          : basePrice;

      const courseRef = await addDoc(collection(db, "courses"), {
        title,
        description,
        category,

        rating: 0,
        review: "",
        learners: 0,
        duration: "",

        price: basePrice,
        discountedPrice,
        discountPercent: discountPercent
          ? Number(discountPercent)
          : 0,

        isFree,
        freePreviewEnabled: freePreview,

        thumbnail: "",
        previewVideo: "",

        sections: sections.filter(
          s => s.title.trim() && s.description.trim()
        ),

        faqs: faqs.filter(
          f => f.q.trim() && f.a.trim()
        ),

        videos: [],

        instructorId: user.uid,
        createdAt: serverTimestamp()
      });

      /* ================= THUMBNAIL UPLOAD ================= */
      if (thumbnail) {
        const thumbRef = ref(
          storage,
          `course-thumbnails/${courseRef.id}`
        );
        await uploadBytes(thumbRef, thumbnail);
        const url = await getDownloadURL(thumbRef);
        await updateDoc(courseRef, { thumbnail: url });
      }

      navigate("/teacherCourse");
    } catch (err) {
      console.error(err);
      alert("Failed to add course");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="add-course-page">
      <div className="add-course-card">
        <h1>Add New Course</h1>

        <div className="form-group">
          <label>Course Title</label>
          <input
            placeholder="Enter course title"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            placeholder="Course description"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">Select category</option>
            <option value="Programming">Programming</option>
            <option value="Design">Design</option>
            <option value="Business">Business</option>
            <option value="Marketing">Marketing</option>
            <option value="Photography">Photography</option>
          </select>
        </div>

        <div className="form-group">
          <label>Price (₹)</label>
          <input
            type="number"
            placeholder="Course price"
            value={price}
            disabled={isFree}
            onChange={e => setPrice(e.target.value)}
          />
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={isFree}
            onChange={e => {
              setIsFree(e.target.checked);
              if (e.target.checked) {
                setPrice(0);
                setDiscountPercent("");
              }
            }}
          />
          <span>Make this course FREE</span>
        </label>

        {!isFree && (
          <div className="form-group">
            <label>Discount (%)</label>
            <input
              type="number"
              placeholder="Optional"
              value={discountPercent}
              onChange={e => setDiscountPercent(e.target.value)}
            />
          </div>
        )}

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={freePreview}
            onChange={e => setFreePreview(e.target.checked)}
          />
          <span>First video free preview</span>
        </label>

        {/* ================= SECTIONS ================= */}
        <div className="sections-box">
          <h3>Course Sections</h3>

          {sections.map((section, index) => (
            <div key={index} className="section-card">
              <input
                placeholder="Section title"
                value={section.title}
                onChange={e =>
                  handleSectionChange(index, "title", e.target.value)
                }
              />

              <textarea
                placeholder="Section description"
                value={section.description}
                onChange={e =>
                  handleSectionChange(index, "description", e.target.value)
                }
              />

              {sections.length > 1 && (
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeSection(index)}
                >
                  Remove Section
                </button>
              )}
            </div>
          ))}

          <button type="button" className="add-btn" onClick={addSection}>
            + Add Section
          </button>
        </div>

        {/* ================= FAQ ================= */}
        <div className="sections-box">
          <h3>Course FAQs</h3>

          {faqs.map((faq, index) => (
            <div key={index} className="section-card">
              <input
                placeholder="Question"
                value={faq.q}
                onChange={e =>
                  handleFaqChange(index, "q", e.target.value)
                }
              />

              <textarea
                placeholder="Answer"
                value={faq.a}
                onChange={e =>
                  handleFaqChange(index, "a", e.target.value)
                }
              />

              {faqs.length > 1 && (
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeFaq(index)}
                >
                  Remove FAQ
                </button>
              )}
            </div>
          ))}

          <button type="button" className="add-btn" onClick={addFaq}>
            + Add FAQ
          </button>
        </div>

        <div className="form-group">
          <label>Thumbnail</label>
          <input
            type="file"
            accept="image/*"
            onChange={e => setThumbnail(e.target.files[0])}
          />
        </div>

        <div className="action-buttons">
          <button className="cancel-btn" onClick={() => navigate(-1)}>
            Cancel
          </button>

          <button className="submit-btn" onClick={handleAddCourse}>
            Create Course
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddCourse;
